/**
 * Base Workflow
 *
 * Common functionality for all workflow types
 */

import { isSuccessResponse, ApiResponse } from '@/lib/utils/api-helpers';
import { transformDocument } from '../transformers';
import type { WorkflowDocument } from '../workflow-types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Extract documents from API response
 * Handles various response structures from backend
 */
export function extractDocuments(
  documentsResponse: ApiResponse | unknown,
  workflowName: string = 'Workflow'
): WorkflowDocument[] {
  let documents: unknown[] = [];

  if (
    isSuccessResponse(documentsResponse as ApiResponse) &&
    isRecord(documentsResponse)
  ) {
    const data = (documentsResponse as unknown as ApiResponse).data;

    // Log the structure to help debug
    console.log(`[${workflowName}] Documents response structure:`, {
      isArray: Array.isArray(data),
      hasFiles: isRecord(data) && !!data.files,
      hasDocuments: isRecord(data) && !!data.documents,
      hasJson: isRecord(data) && !!data.json,
      dataType: typeof data,
      dataKeys: isRecord(data) ? Object.keys(data).slice(0, 10) : [],
      sampleData: Array.isArray(data) && data.length > 0 ? data[0] : null
    });

    // Try multiple extraction methods to handle different response structures
    if (Array.isArray(data)) {
      documents = data;
      console.log(
        `[${workflowName}] Extracted ${documents.length} documents from array response`
      );
    } else if (isRecord(data) && Array.isArray(data.json)) {
      documents = data.json;
      console.log(
        `[${workflowName}] Extracted ${documents.length} documents from json wrapper`
      );
    } else if (isRecord(data) && Array.isArray(data.files)) {
      documents = data.files;
      console.log(
        `[${workflowName}] Extracted ${documents.length} documents from files property`
      );
    } else if (isRecord(data) && Array.isArray(data.documents)) {
      documents = data.documents;
      console.log(
        `[${workflowName}] Extracted ${documents.length} documents from documents property`
      );
    } else if (
      isRecord(data) &&
      isRecord(data.json) &&
      Array.isArray(data.json.files)
    ) {
      documents = data.json.files;
      console.log(
        `[${workflowName}] Extracted ${documents.length} documents from json.files`
      );
    } else {
      console.warn(
        `[${workflowName}] Could not extract documents, unexpected structure:`,
        data
      );
      documents = [];
    }
  } else {
    console.warn(
      `[${workflowName}] Documents response was not successful:`,
      documentsResponse
    );
  }

  return documents.map((d) => transformDocument(d));
}

/**
 * Extract institutions from API response
 * Handles various response structures
 */
export function extractInstitutions(
  institutionsResponse: ApiResponse | unknown
): unknown[] {
  let institutions: unknown[] = [];

  if (isSuccessResponse(institutionsResponse as ApiResponse)) {
    const data = (institutionsResponse as ApiResponse).data;

    if (Array.isArray(data)) {
      institutions = data;
    } else if (
      isRecord(data) &&
      isRecord(data.json) &&
      Array.isArray(data.json.institutions)
    ) {
      institutions = data.json.institutions;
    } else if (isRecord(data) && Array.isArray(data.institutions)) {
      institutions = data.institutions;
    }
  }

  return institutions;
}

/**
 * Extract inventory items from API response
 */
export function extractInventory(
  inventoryResponse: ApiResponse | unknown
): unknown[] {
  let inventory: unknown[] = [];

  if (isSuccessResponse(inventoryResponse as ApiResponse)) {
    const data = (inventoryResponse as ApiResponse).data;

    if (Array.isArray(data)) {
      inventory = data;
    } else if (
      isRecord(data) &&
      isRecord(data.json) &&
      Array.isArray(data.json.items)
    ) {
      inventory = data.json.items;
    } else if (isRecord(data) && Array.isArray(data.items)) {
      inventory = data.items;
    }
  }

  return inventory;
}
